import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Clapperboard, Film, Video, Mic2, Tv, Play, Search, Aperture, Camera } from 'lucide-react';

const Cinematography: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Cinema Arts Institute";
    
    return (
        <div className="min-h-screen bg-[#0a0a0a] font-sans text-stone-300 selection:bg-red-900 selection:text-white">
            {/* Top Bar - "Rec" Light */}
            <div className="fixed top-0 left-0 w-full z-50 px-6 py-4 flex justify-between items-center bg-gradient-to-b from-black/80 to-transparent pointer-events-none">
                 <div className="pointer-events-auto flex items-center gap-3">
                     <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse"></div>
                     <span className="font-bold text-xs uppercase tracking-[0.2em] text-white">Admissions Open</span>
                 </div>
                 <Button variant="ghost" className="pointer-events-auto text-white hover:bg-white/10 rounded-full px-4 h-8 text-[10px] uppercase font-bold tracking-widest border border-white/20">
                     Login
                 </Button>
            </div>

            {/* Side Nav (Vertical on Desktop) */}
            <nav className="fixed left-0 top-0 h-full w-20 hidden lg:flex flex-col items-center justify-between py-8 bg-black border-r border-white/5 z-40">
                <div className="mt-4">
                    <Clapperboard size={24} className="text-white" />
                </div>
                <div className="flex flex-col gap-12 -rotate-90">
                    <a href="#" className="font-bold text-xs uppercase tracking-[0.2em] hover:text-white transition-colors whitespace-nowrap">Student Work</a>
                    <a href="#" className="font-bold text-xs uppercase tracking-[0.2em] hover:text-white transition-colors whitespace-nowrap">Faculty</a>
                    <a href="#" className="font-bold text-xs uppercase tracking-[0.2em] hover:text-white transition-colors whitespace-nowrap text-white">Programs</a>
                </div>
                <div className="mb-4">
                     <Search size={20} className="text-stone-500 hover:text-white cursor-pointer" />
                </div>
            </nav>

            {/* Mobile Nav Top (Visible only on mobile) */}
            <nav className="lg:hidden fixed top-12 w-full z-40 px-6 py-4 bg-black/90 backdrop-blur border-b border-white/10 flex justify-between items-center">
                <span className="font-bold text-lg text-white tracking-widest uppercase">{schoolName}</span>
            </nav>

            {/* Main Content Area */}
            <div className="lg:pl-20">
                
                {/* Hero - Widescreen Layout */}
                <header className="relative min-h-screen flex items-end pb-24 px-8 lg:px-24 overflow-hidden">
                    {/* Background Video Placeholder */}
                    <div className="absolute inset-0 z-0">
                         <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1485846234645-a62644f84728?q=80&w=2059&auto=format&fit=crop')] bg-cover bg-center opacity-40"></div>
                         <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-black/60"></div>
                         <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10"></div> {/* Grain */}
                    </div>

                    <div className="relative z-10 max-w-4xl animate-in fade-in slide-in-from-bottom-10 duration-1000">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="bg-white text-black text-[10px] font-bold px-2 py-0.5 uppercase tracking-widest rounded-sm">Now Screening</span>
                            <span className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Class of 2025 Thesis Films</span>
                        </div>
                        
                        <h1 className="text-5xl md:text-8xl font-bold text-white mb-8 leading-none tracking-tight">
                            Frame Your <br/>
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">Vision.</span>
                        </h1>
                        
                        <p className="text-lg md:text-xl text-stone-400 max-w-xl leading-relaxed mb-12 border-l-2 border-red-600 pl-6">
                            From script to screen. Immerse yourself in the art of visual storytelling with industry-standard cameras, stages, and post-production suites.
                        </p>
                        
                        <div className="flex gap-6">
                            <Button className="bg-white text-black hover:bg-stone-200 font-bold uppercase tracking-widest text-xs h-14 px-8 rounded-sm">
                                Apply for Fall
                            </Button>
                            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 font-bold uppercase tracking-widest text-xs h-14 px-8 rounded-sm bg-transparent flex items-center gap-3">
                                <Play size={12} fill="currentColor" /> Watch Reel
                            </Button>
                        </div>
                    </div>
                </header>

                {/* Horizontal Scrolling Section - Film Strip */}
                <section className="py-24 border-t border-white/5 bg-[#111]">
                    <div className="px-8 lg:px-24 mb-16 flex justify-between items-end">
                        <div>
                            <span className="text-red-600 font-bold uppercase tracking-widest text-xs mb-2 block">Departments</span>
                            <h2 className="text-4xl font-bold text-white">Areas of Focus</h2>
                        </div>
                        <div className="flex gap-2">
                             <div className="w-2 h-2 rounded-full bg-white"></div>
                             <div className="w-2 h-2 rounded-full bg-white/20"></div>
                             <div className="w-2 h-2 rounded-full bg-white/20"></div>
                        </div>
                    </div>

                    <div className="flex gap-6 overflow-x-auto px-8 lg:px-24 pb-8 no-scrollbar snap-x">
                        {[
                            { icon: Aperture, title: "Director's Track", img: "https://images.unsplash.com/photo-1515634928627-2a4e0dae3ddf?q=80&w=2070&auto=format&fit=crop" },
                            { icon: Camera, title: "Cinematography", img: "https://images.unsplash.com/photo-1532715088550-62f09305f765?q=80&w=2070&auto=format&fit=crop" },
                            { icon: Mic2, title: "Sound Design", img: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?q=80&w=2070&auto=format&fit=crop" },
                            { icon: Tv, title: "Post-Production", img: "https://images.unsplash.com/photo-1574375927938-d5a98e8ffe85?q=80&w=2069&auto=format&fit=crop" }
                        ].map((item, i) => (
                            <div key={i} className="min-w-[300px] md:min-w-[400px] snap-center group cursor-pointer relative aspect-[16/9] overflow-hidden rounded-sm border border-white/10">
                                <img src={item.img} alt={item.title} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-40 group-hover:scale-105 transition-all duration-700" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                                <div className="absolute bottom-0 left-0 p-8 w-full">
                                    <item.icon size={32} className="text-white mb-4" />
                                    <h3 className="text-2xl font-bold text-white uppercase tracking-tight">{item.title}</h3>
                                    <div className="w-full h-0.5 bg-red-600 mt-4 max-w-[0px] group-hover:max-w-[100px] transition-all duration-500"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                 {/* Footer */}
                <footer className="bg-black text-stone-500 py-24 px-8 lg:px-24 border-t border-white/5">
                     <div className="max-w-7xl flex flex-col md:flex-row justify-between gap-12">
                         <div>
                             <h2 className="text-3xl font-bold text-white mb-2 uppercase tracking-widest">{schoolName}</h2>
                             <p className="text-xs font-mono uppercase tracking-widest mb-8">Los Angeles • New York • London</p>
                             <div className="flex gap-4">
                                 <Film size={20} className="text-red-600" />
                                 <Video size={20} className="text-red-600" />
                             </div>
                         </div>
                         
                         <div className="grid grid-cols-2 gap-16 font-bold text-xs uppercase tracking-widest">
                             <ul className="space-y-4">
                                 <li><a href="#" className="hover:text-white transition-colors">Festivals</a></li>
                                 <li><a href="#" className="hover:text-white transition-colors">Screenings</a></li>
                                 <li><a href="#" className="hover:text-white transition-colors">Box Office</a></li>
                             </ul>
                             <ul className="space-y-4">
                                 <li><a href="#" className="hover:text-white transition-colors">Industry Partners</a></li>
                                 <li><a href="#" className="hover:text-white transition-colors">Alumni Network</a></li>
                                 <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                             </ul>
                         </div>
                     </div>
                     <div className="mt-20 pt-8 border-t border-white/5 text-[10px] font-mono uppercase text-stone-700 text-center md:text-left">
                         © 2026 {schoolName}. All rights reserved.
                     </div>
                </footer>
            </div>
        </div>
    );
};

export default Cinematography;
